import * as React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import { useNavigate } from 'react-router-dom';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Footer from '../Materials/Footer';
import BurgerBar from '../Materials/BurgerBar';
import Link from '@mui/material/Link';
import TablePagination from '@mui/material/TablePagination';
import Paper from '@mui/material/Paper';
import TableContainer from '@mui/material/TableContainer';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import LinearProgress from '@mui/material/LinearProgress';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import Checkbox from '@mui/material/Checkbox';
import Snackbar from '@mui/material/Snackbar';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';

export default function ImportCSV() {
  const navigate = useNavigate();
  
  // Estados para o upload e processamento
  const [selectedFile, setSelectedFile] = React.useState(null);
  const [uploading, setUploading] = React.useState(false);
  const [previewData, setPreviewData] = React.useState(null);
  const [selectedRecords, setSelectedRecords] = React.useState([]);
  const [saving, setSaving] = React.useState(false);
  
  // Estados para paginação
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  
  // Estados para feedback
  const [snackbar, setSnackbar] = React.useState({
    open: false,
    message: '',
    severity: 'info'
  });

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({
      ...prev,
      open: false
    }));
  };
  
  const [confirmDialog, setConfirmDialog] = React.useState({
    open: false,
    title: '',
    message: '',
    onConfirm: null
  });

  // Manipulador de seleção de arquivo
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'text/csv') {
      setSelectedFile(file);
      // Limpar dados anteriores
      setPreviewData(null);
      setSelectedRecords([]);
    } else {
      setSnackbar({
        open: true,
        message: 'Por favor, selecione um arquivo CSV válido.',
        severity: 'warning'
      });
    }
  };

  // Função para fazer upload do CSV
  const handleUpload = async () => {
    if (!selectedFile) {
      setSnackbar({
        open: true,
        message: 'Por favor, selecione um arquivo primeiro.',
        severity: 'warning'
      });
      return;
    }

    const formData = new FormData();
    formData.append('csvFile', selectedFile);

    setUploading(true);
    try {
      const response = await fetch('https://api.hub4food.colab4food.com/api/advices/upload-csv', {
        method: 'POST',
        body: formData,
      });

      const result = await response.json();
      
      if (result.success) {
        setPreviewData(result.data);
        // Selecionar todos os registros válidos por padrão
        const allValidRecords = result.data.validRecords || [];
        setSelectedRecords(allValidRecords.map((record, index) => index));
        
        setSnackbar({
          open: true,
          message: result.message,
          severity: 'success'
        });
      } else {
        setSnackbar({
          open: true,
          message: `Erro: ${result.error}`,
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Erro ao fazer upload:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao fazer upload do arquivo.',
        severity: 'error'
      });
    } finally {
      setUploading(false);
    }
  };

  // Função para confirmar e salvar os registros selecionados
  const handleSave = async () => {
    if (!previewData || !previewData.validRecords || selectedRecords.length === 0) {
      setSnackbar({
        open: true,
        message: 'There are no selected records to save.',
        severity: 'warning'
      });
      return;
    }

    // Filtrar apenas os registros selecionados
    const recordsToSave = previewData.validRecords.filter((record, index) => 
      selectedRecords.includes(index)
    );

    setConfirmDialog({
      open: true,
      title: 'Confirm Import',
      message: `Are you sure you want to import ${recordsToSave.length} records?`,
      onConfirm: async () => {
        setSaving(true);
        try {
          const response = await fetch('https://api.hub4food.colab4food.com/api/advices/confirm-csv', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ records: recordsToSave }),
          });

          const result = await response.json();
          
          if (result.success) {
            setSnackbar({
              open: true,
              message: result.message,
              severity: 'success'
            });
            
            // Limpar o estado após sucesso
            setTimeout(() => {
              navigate('/createAdvices'); 
            }, 2000);
          } else {
            setSnackbar({
              open: true,
              message: `Error: ${result.error}`,
              severity: 'error'
            });
          }
        } catch (error) {
          console.error('Error saving records:', error);
          setSnackbar({
            open: true,
            message: 'Error saving records.',
            severity: 'error'
          });
        } finally {
          setSaving(false);
          setConfirmDialog({ ...confirmDialog, open: false });
        }
      }
    });
  };

  // Função para cancelar e voltar
  const handleCancel = () => {
    navigate('/createAdvices');
  };

  // Manipulador de seleção/deseleção de registros
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const allIndices = previewData?.validRecords?.map((_, index) => index) || [];
      setSelectedRecords(allIndices);
    } else {
      setSelectedRecords([]);
    }
  };

  const handleSelectRecord = (index) => {
    const currentIndex = selectedRecords.indexOf(index);
    const newSelected = [...selectedRecords];

    if (currentIndex === -1) {
      newSelected.push(index);
    } else {
      newSelected.splice(currentIndex, 1);
    }

    setSelectedRecords(newSelected);
  };

  // Funções para paginação
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Calcular os registros a mostrar na página atual
  const displayedRecords = previewData?.validRecords?.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  ) || [];

  const isAllSelected = previewData?.validRecords?.length > 0 && 
    selectedRecords.length === previewData.validRecords.length;

  // ✅ FUNÇÃO AUXILIAR: Formatador de moeda
  const formatCurrency = (value) => {
    if (!value) return 'N/A';
    
    // Se for número, formata
    if (typeof value === 'number') {
      return `€${value.toLocaleString('pt-PT')}`;
    }
    
    // Se já for string com €, retorna como está
    if (typeof value === 'string' && value.includes('€')) {
      return value;
    }
    
    // Caso contrário, retorna o valor original
    return value;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Navbar */}
      <BurgerBar />
      
      {/* Main Content */}
      <Box component="main" sx={{ flexGrow: 1, py: 4 }}>
        <Container maxWidth="lg">
          <Card>
            <CardContent>
              
              <Box display="flex" alignItems="center" marginBottom={3}>
                <Typography variant="h5">
                  Import Calls from CSV
                </Typography>
              </Box>

              {/* Seção de Upload */}
              <Box sx={{ mb: 4, p: 2, border: '1px dashed #ccc', borderRadius: 1 }}>
                <Typography variant="h6" gutterBottom>
                  1. Select CSV File
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<CloudUploadIcon />}
                  >
                    Select File
                    <input
                      type="file"
                      accept=".csv"
                      hidden
                      onChange={handleFileSelect}
                    />
                  </Button>
                  
                  {selectedFile && (
                    <Typography variant="body1">
                      Selected File: <strong>{selectedFile.name}</strong>
                    </Typography>
                  )}
                </Box>

                <Button
                  variant="contained"
                  onClick={handleUpload}
                  disabled={!selectedFile || uploading}
                  sx={{ mt: 1 }}
                >
                  {uploading ? 'Processing...' : 'Process CSV'}
                </Button>

                {uploading && <LinearProgress sx={{ mt: 2 }} />}
              </Box>

              {/* Resultados */}
              {previewData && (
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h6" gutterBottom>
                    2. Preview of registers
                  </Typography>
                  
                  {/* Resumo */}
                  <Alert 
                    severity="info" 
                    sx={{ mb: 3 }}
                    action={
                      previewData.validRecords.length > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Checkbox
                            checked={isAllSelected}
                            onChange={handleSelectAll}
                            color="primary"
                          />
                          <Typography variant="body2">
                            Select All ({selectedRecords.length}/{previewData.validRecords.length})
                          </Typography>
                        </Box>
                      )
                    }
                  >
                    <AlertTitle>Processing Summary</AlertTitle>
                    <Typography variant="body2">
                      • Total processed rows: {previewData.summary?.totalRows || 0}<br />
                      • Valid records: {previewData.validRecords.length}<br />
                      • Problems found: {previewData.errors.length}<br />
                      • Selected records: {selectedRecords.length}
                    </Typography>
                  </Alert>

                  {/* Lista de Erros */}
                  {previewData.errors.length > 0 && (
                    <Alert
                      severity="warning"
                      sx={{
                        mb: 3,
                        alignItems: 'flex-start',
                        '& .MuiAlert-message': {
                          width: '100%'
                        }
                      }}
                    >
                      <AlertTitle>Problems Found</AlertTitle>
                      <Box
                        sx={{
                          maxHeight: 200,
                          overflowY: 'auto',
                          width: '100%',
                          pr: 1
                        }}
                      >
                        {previewData.errors.map((error, index) => (
                          <Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
                            • {error}
                          </Typography>
                        ))}
                      </Box>
                    </Alert>
                  )}

                  {/* Tabela de Registos Válidos */}
                  {previewData.validRecords.length > 0 && (
                    <>
                      <TableContainer component={Paper} sx={{ maxHeight: 440, mb: 2 }}>
                        <Table stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell padding="checkbox">
                                <Checkbox
                                  checked={isAllSelected}
                                  onChange={handleSelectAll}
                                  color="primary"
                                />
                              </TableCell>
                              <TableCell>Call Title</TableCell>
                              <TableCell>Call ID</TableCell>
                              <TableCell>Deadline</TableCell>
                              {/* ✅ NOVA COLUNA: Maximum Duration */}
                              <TableCell>Max Duration (months)</TableCell>
                              <TableCell>Funding Available</TableCell>
                              <TableCell>Funding Rate</TableCell>
                              <TableCell>Link</TableCell>
                              <TableCell>Type of Call</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {displayedRecords.map((record, index) => {
                              const absoluteIndex = page * rowsPerPage + index;
                              const isSelected = selectedRecords.includes(absoluteIndex);
                              const callType = record.advice_type === '20620f00-12f4-479c-af43-72414e3f6b30' 
                                ? 'National' 
                                : record.advice_type === 'dfc955be-090a-4542-b72a-aed97ce55a7d'
                                ? 'International'
                                : 'Unknown';

                              return (
                                <TableRow 
                                  key={absoluteIndex}
                                  hover
                                  selected={isSelected}
                                  onClick={() => handleSelectRecord(absoluteIndex)}
                                  sx={{ cursor: 'pointer' }}
                                >
                                  <TableCell padding="checkbox">
                                    <Checkbox
                                      checked={isSelected}
                                      color="primary"
                                    />
                                  </TableCell>
                                  <TableCell>{record.title}</TableCell>
                                  <TableCell>{record.advice_title}</TableCell>
                                  <TableCell>{record.submission_period}</TableCell>
                                  {/* ✅ NOVA CÉLULA: Maximum Duration */}
                                  <TableCell>
                                    {record.maximum_duration || 'N/A'}
                                  </TableCell>
                                  <TableCell>
                                    {formatCurrency(record.maximum_allocation)}
                                  </TableCell>
                                  {/* ✅ MODIFICADO: Funding Rate como texto puro */}
                                  <TableCell>
                                    {record.financial_maximum_tax || 'N/A'}
                                  </TableCell>
                                  <TableCell>
                                    {record.link ? (
                                      <Link 
                                        href={record.link} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        Link
                                      </Link>
                                    ) : 'N/A'}
                                  </TableCell>
                                  <TableCell>{callType}</TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>

                      {/* Paginação */}
                      <TablePagination
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        component="div"
                        count={previewData.validRecords.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        labelRowsPerPage="Registers per page:"
                      />

                      {/* Botões de ação */}
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                        <Button
                          variant="outlined"
                          onClick={handleCancel}
                          disabled={saving}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={handleSave}
                          disabled={selectedRecords.length === 0 || saving}
                        >
                          {saving ? 'Saving...' : `Save (${selectedRecords.length})`}
                        </Button>
                      </Box>
                    </>
                  )}

                  {/* Message when no valid records are found */}
                  {previewData.validRecords.length === 0 && (
                    <Alert severity="info">
                      <AlertTitle>No valid records found</AlertTitle>
                      <Typography variant="body2">
                        There are no valid records to import. Please check the errors above and try again.
                      </Typography>
                    </Alert>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Container>
      </Box>

      {/* Footer */}
      <Box>
        <Footer />
      </Box>

      {/* Snackbar para feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Dialog de confirmação */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
      >
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <Typography>{confirmDialog.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button 
            onClick={confirmDialog.onConfirm}
            color="primary"
            variant="contained"
            disabled={saving}
          >
            {saving ? 'Confirming...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}